import { z } from 'zod';
import { AudienceSchema } from './audience';
import { WithTimestampSchema, WithIdSchema } from './utils';
import { objectIdSchema } from './objectId';
import { McmSchema } from './mcm';
import { FeedContentWidgetType } from './const';

export const FeedEditorElementType = ['text', 'image'] as const;

export const BareContentPropertyInfoSchema = z.object({
  _id: objectIdSchema.optional(),
  property: objectIdSchema,
});
export const BasicContentTemplateSchema = z.object({
  type: z.literal('basic').optional(),
});
export const OfferContentTemplateSchema = z.object({
  type: z.literal('offer'),
  description: z.string(),
  images: z.string().array(),
});
export const MessageContentTemplateSchema = z.object({
  type: z.literal('message'),
  description: z.string(),
});
export const InfoContentTemplateSchema = z.object({
  type: z.literal('info'),
  description: z.string(),
  appearance: z.enum(['card', 'questionAndAnswer']),
});

export const EventRundownFilter = z
  .object({
    segments: z.string(),
    levels: z.string(),
    attributes: z.string(),
    venues: z.string(),
  })
  .partial();

export const EventRundownTimeGroupingSchema = z.object({
  name: z.string(),
  startTime: z.number(), // In minutes since day start
});

export const MultiDayConfigSchema = z.object({
  startDate: z.string(),
  endDate: z.string(),
});

export const EventRundownFilterSchema = z
  .object({
    segments: z.array(z.string()),
    levels: z.array(z.string()),
    attributes: z.array(z.string()),
    venues: z.array(z.string()),
  })
  .optional();

export const ContentTemplateParamsSchema = z.object({
  eventId: objectIdSchema.optional(),
  venueId: objectIdSchema.optional(),
  venueRundownConfig: z.string().array().optional(),
  venueMealPeriodsConfig: z.string().array().optional(),
  text: z.string().array().optional(),
  widgetType: z.enum(FeedContentWidgetType).optional(),
  eventRundownFilter: EventRundownFilterSchema.optional(),
  eventRundownTitle: z.string().optional(),
  eventRundownDeckPrefix: z.string().optional(),
  eventRundownTimeGrouping: z.array(EventRundownTimeGroupingSchema).optional(),
  eventRundownRemoveTimeDash: z.boolean().optional(),
  eventRundownShowDescription: z.boolean().optional(),
  multiDayConfig: MultiDayConfigSchema.optional(),
  eventRundownHideEndTime: z.boolean().optional(),
});

export const NewContentTemplateLegacySchema = z.object({
  _id: z.string(),
  name: z.string(),
  content: z.string(),
  property: objectIdSchema,
  hideFromDigitalDailyProgram: z.boolean().optional(),
  params: ContentTemplateParamsSchema.optional(),
  archived: z.boolean().optional(),
  notNative: z.boolean().optional(),
});

export const NewContentTemplateSchema = NewContentTemplateLegacySchema.omit({
  property: true,
})
  .extend({
    brand: objectIdSchema.optional(),
    properties: BareContentPropertyInfoSchema.array(),
    source: objectIdSchema.nullable(),
    tags: z.string().array().optional(),
    mcm: McmSchema.optional(),
    audience: AudienceSchema.optional(),
  })
  .and(
    z
      .union([
        BasicContentTemplateSchema,
        OfferContentTemplateSchema,
        MessageContentTemplateSchema,
        InfoContentTemplateSchema,
      ])
      .optional()
  );

export const BareContentTemplateLegacySchema = NewContentTemplateLegacySchema.and(
  WithTimestampSchema.partial()
);

export const BareContentTemplateSchema = NewContentTemplateSchema.and(WithIdSchema).and(
  WithTimestampSchema.partial()
);

export const VenueRundownConfigSchema = z.object({
  venues: z.string().array(),
});

export type NewContentTemplate = z.infer<typeof NewContentTemplateSchema>;
